import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const NewryAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Newry | Stress Management Near Border</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Newry from Belfast clinic (37 miles). Border city anxiety, cross-border living stress, economic uncertainty. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Newry, stress management Newry, hypnotherapy Newry anxiety, panic attacks Newry, therapist Newry stress, border city anxiety" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newry-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Newry | Stress Management Near Border" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Newry. Specialized support for border city stress, economic uncertainty, cross-border living challenges." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Newry",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Newry from Belfast clinic. Specializing in border city anxiety, cross-border living stress, economic uncertainty, and panic attacks.",
            "url": "https://www.sharonclare-hypnotherapy.com/newry-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Newry",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic (Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Belfast from Newry for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our Belfast clinic is at Belfast Wellness Clinic, 228 Ormeau Road in south Belfast, roughly 40-45 minutes from Newry via the A1 and M1. The A1 and M1 provide a fast, straightforward route with minimal traffic outside peak hours, making regular anxiety treatment sessions accessible for Newry residents. Newry is also on the Belfast rail line, with Botanic and City Hospital the closest stations to the clinic. Online hypnotherapy sessions are available for maximum flexibility."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with border city stress and economic uncertainty?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is particularly effective for the unique stresses of living in a border city like Newry. The ongoing uncertainty around cross-border trade, currency fluctuations, and economic changes creates chronic background anxiety that hypnotherapy can significantly reduce. Solution-focused hypnotherapy works by lowering your overall anxiety baseline, building resilience to ongoing uncertainty, teaching practical stress management techniques, and improving sleep quality that's often disrupted by persistent worry."
                }
              },
              {
                "@type": "Question",
                "name": "Is anxiety treatment affordable for working families in Newry?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Pricing varies by clinic, so we'll go through it during your free initial phone consultation. Most clients see significant improvement within 4-6 sessions, and a discounted 4-session package is available. Payment plans can be discussed."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer online sessions for Newry clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, online hypnotherapy via Zoom is just as effective as in-person sessions and eliminates the 45-minute journey from Newry to Belfast. Many clients start in-person to build rapport, then switch to online for convenience. This is particularly helpful for parents, shift workers, or those with mobility challenges. Online sessions run at the same rate as in-person sessions and use the same evidence-based techniques."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with panic attacks?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for panic attacks. Panic occurs when your brain's threat detection system (the amygdala) becomes overactive, interpreting safe situations as dangerous. Hypnotherapy retrains this response by accessing the subconscious mind directly, calming the fight-or-flight system, reducing the frequency and intensity of panic attacks, building confidence that you can cope with anxiety sensations, and teaching rapid relaxation techniques to interrupt panic spirals. Most clients notice improvement within 3-4 sessions."
                }
              },
              {
                "@type": "Question",
                "name": "How does solution-focused hypnotherapy differ from other anxiety treatments?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Solution-focused hypnotherapy is unique because it works directly with your subconscious mind to create lasting change, rather than just managing symptoms consciously. Unlike talk therapy that analyzes past problems, we focus on your preferred future and how to achieve it. The hypnosis component allows your brain to absorb positive suggestions and relaxation responses at a deeper level, creating faster results than talk therapy alone. It combines neuroscience-based education with guided relaxation to reduce your overall anxiety levels sustainably."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety & Stress Treatment for Newry
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy serving Newry from our Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">37 miles</span> from Newry via A1 • <span className="font-semibold">45 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Fast A1 dual carriageway • Online sessions available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Anxiety Symptoms Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Do You Recognize These Signs of Anxiety?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-primary-50 rounded-xl p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center">
                  <Brain className="w-6 h-6 text-accent-600 mr-2" />
                  Mental Symptoms
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Constant worry about the future (economic uncertainty, border changes)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Racing thoughts that won't switch off at night</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Difficulty concentrating at work or home</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Feeling on edge or irritable without clear cause</span>
                  </li>
                </ul>
              </div>
              <div className="bg-accent-50 rounded-xl p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center">
                  <Heart className="w-6 h-6 text-primary-600 mr-2" />
                  Physical Symptoms
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tight chest, rapid heartbeat, or panic attacks</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Tense muscles, jaw clenching, or tension headaches</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Disrupted sleep (can't fall asleep or wake at 3am worrying)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Stomach issues, nausea, or digestive problems from stress</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl">
              <h3 className="font-bold text-lg text-gray-900 mb-2">Border City Stress</h3>
              <p className="text-gray-700 leading-relaxed">
                Living in Newry brings unique anxiety triggers: uncertainty about cross-border trade regulations, currency fluctuations affecting household budgets, concerns about border infrastructure changes, economic pressures from ongoing political uncertainty, and the background stress of living at a geographical and cultural border. These aren't "all in your head" - they're real environmental stressors that accumulate over time. Hypnotherapy helps your brain process and respond to these stresses more calmly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Relieves Anxiety & Stress
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8 text-center">
              Anxiety happens when your brain's threat detection system (the amygdala) becomes overactive, perceiving danger where there is none. Solution-focused hypnotherapy retrains this response.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Calm the Amygdala</h3>
                <p className="text-gray-700 leading-relaxed">
                  Hypnosis directly accesses the subconscious mind where anxiety patterns are stored, helping to reduce your brain's overactive threat response and restore a calmer baseline.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mb-4">
                  <Moon className="w-6 h-6 text-accent-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Improve Sleep Quality</h3>
                <p className="text-gray-700 leading-relaxed">
                  Anxiety disrupts sleep, which worsens anxiety - a vicious cycle. Hypnotherapy breaks this pattern by teaching your brain new relaxation responses, restoring restorative sleep.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Build Resilience</h3>
                <p className="text-gray-700 leading-relaxed">
                  Rather than eliminating all stress (impossible in border city living), hypnotherapy builds your capacity to handle stress without it overwhelming you or triggering panic.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="font-bold text-xl text-gray-900 mb-4 text-center">What Happens in a Session?</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-lg text-gray-900 mb-2">Part 1: Understanding (30 min)</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We discuss what's been happening with your anxiety, what triggers it, and what you want to achieve. I explain the neuroscience of anxiety in simple terms so you understand why you feel this way and how we'll address it.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-lg text-gray-900 mb-2">Part 2: Hypnosis (30 min)</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    You'll sit comfortably while I guide you into deep relaxation (similar to the feeling just before sleep). In this relaxed state, your subconscious mind is receptive to positive suggestions for calm, confidence, and control. You remain aware and in control throughout.
                  </p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4 text-center">
                Most clients see improvement within 4-6 sessions, with each session building on the last to create lasting change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Newry
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-primary-50 rounded-xl p-8">
                <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center">
                  <Car className="w-6 h-6 text-accent-600 mr-2" />
                  By Car (45 minutes)
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>From Newry:</strong> Take A1 northbound towards Belfast (37 miles, fast dual carriageway)</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Route:</strong> Take the A1 and M1 into Belfast, then head south along the Ormeau Road (A24) - the clinic is at 228 Ormeau Road</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>By train:</strong> Newry is on the Belfast line - Botanic and City Hospital are the closest stations to the clinic, with Lanyon Place serving regional routes</span>
                  </li>
                  <li className="flex items-start">
                    <Clock className="w-5 h-5 text-accent-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span><strong>Journey time:</strong> 45 minutes (minimal traffic outside peak hours)</span>
                  </li>
                </ul>
              </div>
              <div className="bg-accent-50 rounded-xl p-8">
                <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-6 h-6 text-primary-600 mr-2" />
                  Online Option
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Can't make the journey to Belfast? Online hypnotherapy via Zoom is just as effective as in-person sessions. Many Newry clients start with one in-person visit to build rapport, then switch to online for convenience.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Benefits of online:</strong> Eliminates the 90-minute round trip journey, flexibility for shift workers and parents, accessible for those with mobility challenges, the same per-session rate with no travel costs.
                </p>
                <a
                  href="/book"
                  className="btn-primary inline-flex items-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Online Session
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Newry and South Armagh Clients Say
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "The anxiety from constant border uncertainty was affecting my sleep and my work. After 5 sessions with Sharon, I feel like a different person. I can't control the political situation, but I can control how I respond to it."
                </p>
                <p className="font-semibold text-gray-900">M.K., Newry</p>
                <p className="text-sm text-gray-600">Border city stress & insomnia</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "I was having panic attacks driving over the border for work. Sharon's approach is so practical and science-based, not the 'woo-woo' stuff I was worried about. The panic attacks stopped after 4 sessions."
                </p>
                <p className="font-semibold text-gray-900">L.D., South Armagh</p>
                <p className="text-sm text-gray-600">Panic attacks & work anxiety</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <p className="text-gray-700 leading-relaxed mb-4 italic">
                  "The 45-minute drive to Belfast is worth it. Sharon's background as a nurse means she really understands anxiety from a medical perspective. I tried counseling for years with limited results - hypnotherapy worked faster."
                </p>
                <p className="font-semibold text-gray-900">C.O., Warrenpoint</p>
                <p className="text-sm text-gray-600">Generalized anxiety disorder</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Why Newry Clients Choose Sharon Clare Hypnotherapy
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Medical Background</h3>
                  <p className="text-gray-700 leading-relaxed">
                    15 years as a nurse at Belfast City Hospital means I understand anxiety from a medical perspective, not just a therapeutic one.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Accessible Pricing</h3>
                  <p className="text-gray-700 leading-relaxed">
                    A discounted 4-session package is available. Affordable for working families without compromising professional quality.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">CNHC Registered</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Professional registration, full insurance, and adherence to strict ethical guidelines ensure your safety and quality of care.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">Online Flexibility</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Can't make the drive to Belfast? Online sessions via Zoom are just as effective and eliminate the 90-minute round trip.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </main>
  );
};

export default NewryAnxietyStress;
